import type { MutateOptions, QueryClient } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import {
  Cart as CartConfig,
  useLocalStorage,
  UserLocale,
} from 'hooks/userPreferences';
import { useCallback } from 'react';
import type { AddDiscountMutation, CreateCartMutation } from 'services/shopify';
import { ShopifyService } from 'services/shopify';
import type { Cart } from 'types/cart/Cart';
import { convertToCountryCodeEnum } from 'utils/convertToCountryCodeEnum';
import { mapShopifyCart } from 'utils/mappers/mapShopifyCart';

import { CART_KEY } from './useCart';

type DiscountCart = NonNullable<
  AddDiscountMutation['cartDiscountCodesUpdate']
>['cart'];
type CreatedCart = NonNullable<CreateCartMutation['cartCreate']>['cart'];

type ResultCart = DiscountCart | CreatedCart;

type DiscountMutateOptions = MutateOptions<
  ResultCart | undefined,
  Error,
  string[]
>;

/**
 * Replaces the cart's discount codes with exactly `discountCodes`. This is the
 * raw Shopify `cartDiscountCodesUpdate` behaviour - it does NOT merge.
 *
 * When there is no session cart yet, a cart is created carrying the codes
 * (`cartCreate` accepts `discountCodes`), so a code can be applied before the
 * shopper has added anything - e.g. a `discount` URL param on a landing page.
 */
async function setDiscountCodes(
  discountCodes: string[],
  countryCode: string,
  setCartId: (cartId: { id: string }) => void,
  getCartId: () => { id?: string } | null,
): Promise<ResultCart | undefined> {
  const { id: cartId } = getCartId() || {};

  if (cartId) {
    const response = await ShopifyService.addDiscount({
      cartId,
      discountCodes,
    });

    if (response?.cartDiscountCodesUpdate?.cart?.id) {
      setCartId({ id: response.cartDiscountCodesUpdate.cart.id });
    }

    return response.cartDiscountCodesUpdate?.cart;
  }

  const { cartCreate } = await ShopifyService.createCart({
    input: {
      buyerIdentity: { countryCode: convertToCountryCodeEnum(countryCode) },
      discountCodes,
    },
  });

  if (cartCreate?.cart?.id) {
    setCartId({ id: cartCreate.cart.id });
  }

  return cartCreate?.cart;
}

// Codes on the cart are upper-cased by `cleanDiscountCodes`, so we normalise
// incoming codes the same way to keep union/subtract dedup consistent. We also
// trim whitespace and drop empties so `SAVE10` and `SAVE10 ` aren't treated as
// distinct codes (matches `normalizePromoCode`).
const normalize = (codes: string[]) =>
  codes.map((code) => code.trim().toUpperCase()).filter(Boolean);

/**
 * Manages the cart's discount codes.
 *
 * Shopify's `cartDiscountCodesUpdate` mutation *replaces* the cart's entire set
 * of discount codes with whatever array it receives. To stop one caller wiping
 * out codes applied by another (e.g. two product blocks, each with its own promo
 * code), every operation reads the cart's current codes from the query cache and
 * derives the next list from them:
 *   - `addDiscounts`    - union of current + given codes (the safe default)
 *   - `removeDiscounts` - current codes minus the given codes
 *
 * Each accepts react-query mutate options (e.g. `onSettled`).
 */
export const useDiscounts = (queryClient: QueryClient) => {
  const {
    value: { countryCode },
    getValue: getLocale,
  } = useLocalStorage(UserLocale);
  const { getValue, setStoredValue: setCartId } = useLocalStorage(CartConfig);

  const { mutate, isPending } = useMutation({
    // Read the country fresh from storage rather than the rendered
    // `countryCode`: on a `?country=…&discount=…` landing page the locale
    // selector writes the country to storage after this hook has hydrated its
    // own (non-reactive) copy, so the rendered value can be stale when the cart
    // is created. Fall back to the rendered value if nothing is stored yet.
    mutationFn: (discountCodes: string[]) =>
      setDiscountCodes(
        discountCodes,
        getLocale()?.countryCode ?? countryCode,
        setCartId,
        getValue,
      ),
    onSuccess: (cart) => {
      if (cart) {
        queryClient.setQueriesData(
          { queryKey: [CART_KEY, countryCode] },
          mapShopifyCart(cart),
        );
      }
    },
  });

  const getCurrentCodes = useCallback(
    () =>
      queryClient
        .getQueryData<Cart>([CART_KEY, countryCode])
        ?.discountCodes.map((dC) => dC.code) ?? [],
    [queryClient, countryCode],
  );

  const addDiscounts = useCallback(
    (codes: string[], options?: DiscountMutateOptions) =>
      mutate(
        Array.from(new Set([...getCurrentCodes(), ...normalize(codes)])),
        options,
      ),
    [mutate, getCurrentCodes],
  );

  const removeDiscounts = useCallback(
    (codes: string[], options?: DiscountMutateOptions) => {
      const toRemove = new Set(normalize(codes));
      mutate(
        getCurrentCodes().filter((code) => !toRemove.has(code)),
        options,
      );
    },
    [mutate, getCurrentCodes],
  );

  return { addDiscounts, removeDiscounts, isPending };
};
